import { useMemo, useState } from "react";
import moment from "moment";
import { Search, X, SlidersHorizontal, CalendarRange } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import SegmentedControl from "./SegmentedControl";
import FilterMultiSelect from "./FilterMultiSelect";
import ProviderSelect from "./ProviderSelect";
import {
  STATUS_LABEL,
  STATUS_OPTIONS,
  VISIT_TYPE_LABEL,
  VISIT_TYPE_OPTIONS,
  DELAY_STATUS_LABEL,
  DELAY_STATUS_OPTIONS,
} from "../lib/appointment-filters";
import { cn } from "../lib/utils";

const ISO = "YYYY-MM-DD";

export const DATE_PRESETS = [
  { value: "any", label: "Any", range: () => ["", ""] },
  { value: "today", label: "Today", range: () => [moment().format(ISO), moment().format(ISO)] },
  {
    value: "tomorrow",
    label: "Tomorrow",
    range: () => [moment().add(1, "day").format(ISO), moment().add(1, "day").format(ISO)],
  },
  {
    value: "week",
    label: "This Week",
    range: () => [moment().startOf("week").format(ISO), moment().endOf("week").format(ISO)],
  },
  { value: "month", label: "Month", range: null },
  { value: "custom", label: "Custom", range: null },
];

const MONTH_NAMES = moment.months();

/** Which preset, if any, the current from/to pair corresponds to. */
export function presetFor(from, to) {
  if (!from && !to) return "any";
  const match = DATE_PRESETS.find((p) => {
    if (!p.range) return false;
    const [f, t] = p.range();
    return f === from && t === to;
  });
  return match ? match.value : "custom";
}

/** A single applied-filter pill. */
function Chip({ group, label, onRemove }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card py-1 pl-2.5 pr-1 text-xs">
      <span className="text-muted-foreground">{group}</span>
      <span className="font-medium text-foreground">{label}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${group} ${label} filter`}
        className="rounded-full p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

/**
 * The Appointments filter bar.
 *
 * `value` is the full filter state; `patch` merges a partial update. Every
 * applied filter is echoed back as a removable chip, so what's narrowing the
 * list is always visible rather than hidden inside closed dropdowns.
 */
export default function AppointmentFilters({
  value,
  patch,
  onReset,
  doctors,
  departments = [],
  agents = [],
  showProvider = true,
}) {
  const {
    search,
    from,
    to,
    doctorId,
    departmentIds = [],
    statuses = [],
    visitTypes = [],
    delayStatuses = [],
    agentIds = [],
  } = value;

  const [customMode, setCustomMode] = useState(false);
  const [monthMode, setMonthMode] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const derivedPreset = presetFor(from, to);
  const preset = customMode
    ? "custom"
    : monthMode
      ? "month"
      : derivedPreset === "custom"
        ? "custom"
        : derivedPreset;
  const monthValue = from ? from.slice(0, 7) : moment().format("YYYY-MM");
  const [selectedYear, selectedMonthIdx] = monthValue.split("-").map(Number);
  const selectedDoctor = doctors.find((d) => d._id === doctorId);
  const departmentOptions = useMemo(
    () => departments.map((d) => ({ value: d._id, label: d.name })),
    [departments]
  );
  const agentOptions = useMemo(() => agents.map((a) => ({ value: a._id, label: a.name })), [agents]);

  const chips = useMemo(() => {
    const out = [];

    if (from || to) {
      const label =
        from && to
          ? from === to
            ? moment(from, ISO).format("D MMM YYYY")
            : `${moment(from, ISO).format("D MMM")} – ${moment(to, ISO).format("D MMM YYYY")}`
          : from
            ? `From ${moment(from, ISO).format("D MMM YYYY")}`
            : `Until ${moment(to, ISO).format("D MMM YYYY")}`;
      out.push({ key: "date", group: "Date", label, remove: () => patch({ from: "", to: "" }) });
    }

    if (showProvider && selectedDoctor) {
      out.push({
        key: "doctor",
        group: "Provider",
        label: selectedDoctor.name,
        remove: () => patch({ doctorId: "" }),
      });
    }

    departmentIds.forEach((id) => {
      const dept = departments.find((d) => d._id === id);
      out.push({
        key: `department-${id}`,
        group: "Department",
        label: dept?.name || id,
        remove: () => patch({ departmentIds: departmentIds.filter((v) => v !== id) }),
      });
    });

    statuses.forEach((s) =>
      out.push({
        key: `status-${s}`,
        group: "Status",
        // STATUS_OPTIONS also covers the computed "Missed"/"Delay" statuses.
        label: STATUS_OPTIONS.find((o) => o.value === s)?.label || STATUS_LABEL[s] || s,
        remove: () => patch({ statuses: statuses.filter((v) => v !== s) }),
      })
    );

    visitTypes.forEach((t) =>
      out.push({
        key: `type-${t}`,
        group: "Type",
        label: VISIT_TYPE_LABEL[t] || t,
        remove: () => patch({ visitTypes: visitTypes.filter((v) => v !== t) }),
      })
    );

    delayStatuses.forEach((d) =>
      out.push({
        key: `delay-${d}`,
        group: "Delay",
        label: DELAY_STATUS_LABEL[d] || d,
        remove: () => patch({ delayStatuses: delayStatuses.filter((v) => v !== d) }),
      })
    );

    agentIds.forEach((id) => {
      const agent = agents.find((a) => a._id === id);
      out.push({
        key: `agent-${id}`,
        group: "Booked by",
        label: agent?.name || id,
        remove: () => patch({ agentIds: agentIds.filter((v) => v !== id) }),
      });
    });

    return out;
  }, [
    from,
    to,
    showProvider,
    selectedDoctor,
    departmentIds,
    departments,
    statuses,
    visitTypes,
    delayStatuses,
    agentIds,
    agents,
    patch,
  ]);

  const applyMonth = (year, month1to12) => {
    const start = moment(`${year}-${month1to12}`, "YYYY-M").startOf("month").format(ISO);
    const end = moment(`${year}-${month1to12}`, "YYYY-M").endOf("month").format(ISO);
    patch({ from: start, to: end });
  };

  const applyPreset = (key) => {
    if (key === "custom") {
      // "Custom" reveals the date inputs; seed them with today so the range
      // is valid the moment it appears rather than querying an empty range.
      setCustomMode(true);
      setMonthMode(false);
      if (!from && !to) {
        const today = moment().format(ISO);
        patch({ from: today, to: today });
      }
      return;
    }
    if (key === "month") {
      // "Month" reveals a Year + Month picker; seed with the current month.
      setCustomMode(false);
      setMonthMode(true);
      applyMonth(moment().year(), moment().month() + 1);
      return;
    }
    setCustomMode(false);
    setMonthMode(false);
    const chosen = DATE_PRESETS.find((p) => p.value === key);
    if (!chosen?.range) return;
    const [f, t] = chosen.range();
    patch({ from: f, to: t });
  };

  // Rendered twice — inline from `sm` up, and inside the filter modal below
  // it — so `idPrefix` keeps the date inputs' ids (and their labels) unique.
  const renderControls = (idPrefix, stacked) => (
    <>
      {/* inline, the provider sits beside search instead */}
      {stacked && showProvider && (
        <ProviderSelect doctors={doctors} value={doctorId} onChange={(v) => patch({ doctorId: v })} />
      )}

      {/* date presets + filter dropdowns */}
      <div className="flex flex-wrap items-center gap-2">
        <SegmentedControl items={DATE_PRESETS} value={preset} onChange={applyPreset} size="sm" />

        {!stacked && (
          <>
            <span className="mx-1 hidden h-5 w-px bg-border sm:block" aria-hidden="true" />
            <SlidersHorizontal className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          </>
        )}
        {departmentOptions.length > 0 && (
          <FilterMultiSelect
            label="Department"
            options={departmentOptions}
            value={departmentIds}
            onChange={(v) => patch({ departmentIds: v })}
          />
        )}
        <FilterMultiSelect
          label="Status"
          options={STATUS_OPTIONS}
          value={statuses}
          onChange={(v) => patch({ statuses: v })}
        />
        <FilterMultiSelect
          label="Type"
          options={VISIT_TYPE_OPTIONS}
          value={visitTypes}
          onChange={(v) => patch({ visitTypes: v })}
        />
        <FilterMultiSelect
          label="Delay / Outcome"
          options={DELAY_STATUS_OPTIONS}
          value={delayStatuses}
          onChange={(v) => patch({ delayStatuses: v })}
        />
        {agentOptions.length > 0 && (
          <FilterMultiSelect
            label="Booked by"
            options={agentOptions}
            value={agentIds}
            onChange={(v) => patch({ agentIds: v })}
          />
        )}
      </div>

      {/* custom date range */}
      {preset === "custom" && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-border bg-muted/30 p-2.5">
          <CalendarRange className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <label htmlFor={`${idPrefix}-from`} className="text-xs font-medium text-muted-foreground">
            From
          </label>
          <Input
            id={`${idPrefix}-from`}
            type="date"
            value={from}
            max={to || undefined}
            onChange={(e) => patch({ from: e.target.value })}
            className="h-8 w-[10rem] bg-card text-sm"
          />
          <label htmlFor={`${idPrefix}-to`} className="text-xs font-medium text-muted-foreground">
            To
          </label>
          <Input
            id={`${idPrefix}-to`}
            type="date"
            value={to}
            min={from || undefined}
            onChange={(e) => patch({ to: e.target.value })}
            className="h-8 w-[10rem] bg-card text-sm"
          />
        </div>
      )}

      {/* year + month picker */}
      {preset === "month" && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-border bg-muted/30 p-2.5">
          <CalendarRange className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <label htmlFor={`${idPrefix}-month`} className="text-xs font-medium text-muted-foreground">
            Month
          </label>
          <select
            id={`${idPrefix}-month`}
            value={selectedMonthIdx}
            onChange={(e) => applyMonth(selectedYear, Number(e.target.value))}
            className="h-8 rounded-md border border-input bg-card px-2 text-sm"
          >
            {MONTH_NAMES.map((name, idx) => (
              <option key={name} value={idx + 1}>
                {name}
              </option>
            ))}
          </select>
          <label htmlFor={`${idPrefix}-year`} className="text-xs font-medium text-muted-foreground">
            Year
          </label>
          <select
            id={`${idPrefix}-year`}
            value={selectedYear}
            onChange={(e) => applyMonth(Number(e.target.value), selectedMonthIdx)}
            className="h-8 rounded-md border border-input bg-card px-2 text-sm"
          >
            {Array.from({ length: 6 }, (_, i) => moment().year() - 3 + i).map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      )}
    </>
  );

  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="space-y-3 p-4">
        {/* search (+ filter button below `sm`, + provider from `sm` up) */}
        <div className="flex gap-2 sm:gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => patch({ search: e.target.value })}
              placeholder="Search by patient, phone, or doctor…"
              className="h-9 pl-9 pr-9"
            />
            {search && (
              <button
                type="button"
                onClick={() => patch({ search: "" })}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setFiltersOpen(true)}
            aria-label={chips.length ? `Filters (${chips.length} applied)` : "Filters"}
            className="relative h-9 w-9 shrink-0 bg-card sm:hidden"
          >
            <SlidersHorizontal className="h-4 w-4" />
            {chips.length > 0 && (
              <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-primary-foreground">
                {chips.length}
              </span>
            )}
          </Button>
          {showProvider && (
            <div className="hidden w-60 sm:block">
              <ProviderSelect doctors={doctors} value={doctorId} onChange={(v) => patch({ doctorId: v })} />
            </div>
          )}
        </div>

        <div className="hidden space-y-3 sm:block">{renderControls("filter", false)}</div>
      </div>

      <Dialog open={filtersOpen} onOpenChange={setFiltersOpen}>
        <DialogContent className="w-[calc(100%-2rem)] gap-5 p-5">
          <DialogHeader>
            <DialogTitle>Filters</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">{renderControls("filter-modal", true)}</div>
          <div className="flex items-center justify-between gap-2">
            <Button variant="ghost" size="sm" onClick={onReset} disabled={chips.length === 0}>
              Clear all
            </Button>
            <Button size="sm" onClick={() => setFiltersOpen(false)}>
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* applied filters */}
      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-t border-border bg-muted/20 px-4 py-2.5">
          <span className="text-xs font-medium text-muted-foreground">Filtered by</span>
          {chips.map((chip) => (
            <Chip key={chip.key} group={chip.group} label={chip.label} onRemove={chip.remove} />
          ))}
          <Button
            variant="ghost"
            size="sm"
            onClick={onReset}
            className={cn("ml-auto h-7 px-2 text-xs text-muted-foreground hover:text-foreground")}
          >
            Clear all
          </Button>
        </div>
      )}
    </div>
  );
}
